import React from "react";
import { createPortal } from "react-dom";
import { Star, X, Loader2, MessageSquareHeart, Send } from "lucide-react";
import { submitReview } from "../../api/reviews.js";

/**
 * Soft review / feedback prompt used on sourcing + post-submit.
 */
export default function ReviewPromptModal({
  open,
  onClose,
  onSubmitted,
  productCategory = null,
  product = null,
  pageUrl = null,
  contextLabel = "How was your experience?",
  defaultType = "General Feedback",
}) {
  const [rating, setRating] = React.useState(0);
  const [hover, setHover] = React.useState(0);
  const [text, setText] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [done, setDone] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setRating(0);
    setHover(0);
    setText("");
    setError("");
    setDone(false);
    setLoading(false);
  }, [open]);

  React.useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  if (!open) return null;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!text.trim()) {
      setError("Please enter your feedback.");
      return;
    }
    setLoading(true);
    try {
      await submitReview({
        review_text: text.trim(),
        rating: rating > 0 ? rating : undefined,
        review_type: defaultType,
        product_category: productCategory || undefined,
        product: product || undefined,
        page_url: pageUrl || (typeof window !== "undefined" ? window.location.pathname : undefined),
      });
      setDone(true);
      onSubmitted?.();
      setTimeout(() => onClose?.(), 1400);
    } catch (err) {
      setError(err?.message || "Could not submit feedback. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[960] flex items-center justify-center p-3 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="review-prompt-title"
    >
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
        onClick={loading ? undefined : onClose}
        aria-hidden="true"
      />
      <div className="relative z-10 flex w-full max-w-[320px] sm:max-w-md max-h-[min(85dvh,520px)] sm:max-h-[min(88dvh,640px)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
        <button
          type="button"
          onClick={onClose}
          disabled={loading}
          className="absolute top-2.5 right-2.5 sm:top-3 sm:right-3 z-20 inline-flex h-7 w-7 sm:h-8 sm:w-8 items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 disabled:opacity-50 touch-manipulation"
          aria-label="Close"
        >
          <X className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
        </button>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          {done ? (
            <div className="px-5 py-8 text-center sm:px-8 sm:py-10">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100">
                <Star className="h-6 w-6 fill-emerald-600 text-emerald-600" />
              </div>
              <p className="text-sm font-semibold text-slate-900">Thanks for your feedback</p>
              <p className="mt-1 text-xs text-slate-500">It helps us improve Dynesourcer.</p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="px-5 pt-8 pb-5 sm:px-6 sm:pt-9 sm:pb-6">
              <div className="flex flex-col items-center text-center pr-6">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-sky-100 text-sky-600">
                  <MessageSquareHeart className="h-6 w-6" />
                </div>
                <h2 id="review-prompt-title" className="mt-3 text-[17px] sm:text-lg font-bold text-slate-900 leading-snug">
                  Please share feedback
                </h2>
                <p className="mt-1 text-xs sm:text-sm text-slate-500 leading-snug line-clamp-2">
                  {contextLabel}
                </p>
              </div>

              <div className="mt-4">
                <div className="mb-1.5 text-center text-xs font-medium text-slate-600">Rating</div>
                <div className="flex items-center justify-center gap-0">
                  {[1, 2, 3, 4, 5].map((n) => {
                    const active = (hover || rating) >= n;
                    return (
                      <button
                        key={n}
                        type="button"
                        onMouseEnter={() => setHover(n)}
                        onMouseLeave={() => setHover(0)}
                        onFocus={() => setHover(n)}
                        onBlur={() => setHover(0)}
                        onClick={() => setRating(n === rating ? 0 : n)}
                        className="flex h-9 w-9 items-center justify-center rounded-lg touch-manipulation active:scale-95"
                        aria-label={`${n} star${n > 1 ? "s" : ""}`}
                      >
                        <Star
                          className={`h-6 w-6 sm:h-7 sm:w-7 ${
                            active ? "fill-amber-400 text-amber-400" : "text-slate-300"
                          }`}
                        />
                      </button>
                    );
                  })}
                </div>
              </div>

              <label className="mt-4 block">
                <span className="text-xs font-medium text-slate-600">Your feedback</span>
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  rows={3}
                  className="mt-1.5 w-full resize-none rounded-lg border border-slate-200 px-2.5 py-2 text-[16px] outline-none focus:ring-2 focus:ring-sky-200 sm:rounded-xl sm:px-3 sm:py-2.5 sm:text-sm"
                  placeholder="Tell us what worked well or what we can improve..."
                  disabled={loading}
                  required
                />
              </label>

              {error && <p className="mt-2 text-[11px] sm:text-xs text-rose-600">{error}</p>}

              <div className="mt-4 space-y-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl bg-sky-500 px-4 py-2.5 text-sm font-bold text-white hover:bg-sky-600 disabled:opacity-70 touch-manipulation"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {loading ? "Sending..." : "Send feedback"}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                  className="inline-flex min-h-[40px] w-full items-center justify-center rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 touch-manipulation"
                >
                  Not now
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
